import React, { useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Link,
  CircularProgress,
  TextField,
  InputAdornment,
  TablePagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';

interface ClientRevenueData {
  clientId: string;
  clientName: string;
  totalRevenue: number;
  serviceCount: number;
  serviceTypes: string[];
  avgContractValue: number;
}

interface ClientRevenueAnalysisProps {
  data: ClientRevenueData[];
  isLoading: boolean;
}

const ClientRevenueAnalysis: React.FC<ClientRevenueAnalysisProps> = ({ data, isLoading }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Calculate max revenue - safe to do before early returns as it uses data prop
  const maxRevenue = data && data.length > 0 ? Math.max(...data.map(client => client.totalRevenue)) : 0;

  // Filter data based on search term - must be before early returns
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (!searchTerm) return data;
    const lowerSearch = searchTerm.toLowerCase();
    return data.filter(client => 
      client.clientName?.toLowerCase().includes(lowerSearch) ||
      client.serviceTypes.some(type => type.toLowerCase().includes(lowerSearch))
    );
  }, [data, searchTerm]);

  // Paginate filtered data - must be before early returns
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handleClientClick = (clientId: string) => {
    navigate(`/utilities/clients/${clientId}`);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getRevenueColor = (revenue: number, maxRevenue: number) => {
    const percentage = (revenue / maxRevenue) * 100;
    if (percentage >= 80) return 'success';
    if (percentage >= 50) return 'warning';
    return 'default';
  };

  // Early returns after all hooks are defined
  if (isLoading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: '#fff' }}>Loading client revenue data...</Typography>
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography sx={{ color: '#fff' }}>No client revenue data available</Typography>
      </Paper>
    );
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#fff' }}>
          Top Revenue Clients
        </Typography>
        <TextField
          size="small"
          placeholder="Search clients..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
              </InputAdornment>
            ),
            sx: {
              color: '#fff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.5)'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.7)'
              }
            }
          }}
          sx={{ width: 300 }}
        />
      </Box>
      
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Rank</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Client</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Total Revenue</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Services</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Service Types</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Avg Contract</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((client, index) => {
              const actualRank = page * rowsPerPage + index + 1;
              return (
              <TableRow key={client.clientId} hover>
                <TableCell>
                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '1rem' }}>
                    #{actualRank}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Link
                    component="button"
                    variant="inherit"
                    onClick={() => handleClientClick(client.clientId)}
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontWeight: 'bold',
                      color: '#fff',
                      fontSize: '0.875rem',
                      '&:hover': {
                        textDecoration: 'none',
                        color: '#e0e0e0'
                      }
                    }}
                  >
                    {client.clientName || 'Unknown Client'}
                  </Link>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.875rem' }}>
                      {formatCurrency(client.totalRevenue)}
                    </Typography>
                    <Chip
                      label={`${Math.round((client.totalRevenue / maxRevenue) * 100)}%`}
                      color={getRevenueColor(client.totalRevenue, maxRevenue)}
                      size="small"
                      sx={{ minWidth: '50px' }}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                    {client.serviceCount}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {client.serviceTypes.slice(0, 3).map((serviceType, idx) => (
                      <Chip
                        key={idx}
                        label={serviceType}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: '#fff',
                          borderColor: '#fff',
                          fontSize: '0.7rem'
                        }}
                      />
                    ))}
                    {client.serviceTypes.length > 3 && (
                      <Chip
                        label={`+${client.serviceTypes.length - 3}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: '#fff',
                          borderColor: '#fff',
                          fontSize: '0.7rem'
                        }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                    {formatCurrency(client.avgContractValue)}
                  </Typography>
                </TableCell>
              </TableRow>
            )})}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 20, 50, 100]}
        sx={{
          color: '#fff',
          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
            color: '#fff'
          },
          '.MuiTablePagination-select': {
            color: '#fff'
          },
          '.MuiTablePagination-selectIcon': {
            color: '#fff'
          },
          '.MuiIconButton-root': {
            color: '#fff'
          }
        }}
      />
      
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ color: '#bbb' }}>
          {searchTerm ? `Found ${filteredData.length} matching clients` : `Total ${data.length} clients`}
        </Typography>
        <Typography variant="body2" sx={{ color: '#bbb' }}>
          Total Revenue: {formatCurrency(filteredData.reduce((sum, client) => sum + client.totalRevenue, 0))}
        </Typography>
      </Box>
    </Paper>
  );
};

export { ClientRevenueAnalysis };
